import feathersClient from '@/feathers-client'
import { calcCategoryQuestionRating } from './calcCategoryQuestionRating'

/**
 * Normalizes Feathers response to always be an array
 * @param {Array|Object} response - API response from Feathers
 * @returns {Array} Normalized array of items
 */
const normalizeResponse = (response) => {
  if (Array.isArray(response)) {
    return response
  }
  return response?.data || []
}

/**
 * Gets questions for a specific category using its subcategories
 * @param {string|number} compassCategoryId - The ID of the category
 * @param {Array} questions - Array of question objects
 * @param {Array} compassSubCategories - Array of subcategory objects
 * @returns {Array} Questions belonging to the category
 */
const getCompassCategoryQuestions = (compassCategoryId, questions, compassSubCategories) => {
  return questions.filter(q => {
    // Get the sub-category of the question
    const subCategory = compassSubCategories.find(sc => sc.id === q.compassSubCategoryId)
    // Return items where sub-category belongs to the current category
    return subCategory && subCategory.compassCategoryId === compassCategoryId
  })
}

/**
 * Gets objective question ratings for multiple categories in a compass
 * @param {number} frameworkId - The ID of the framework
 * @param {string|number} compassId - The ID of the compass
 * @param {Array} compassCategories - Array of category objects
 * @returns {Promise<Array<number>>} Array of category objective question ratings
 */
export const getCompassCategoryQuestionObligatoryRatings = async (frameworkId, compassId, compassCategories) => {
  // Validate required parameters
  if (!compassId || !frameworkId || !compassCategories || compassCategories.length === 0) {
    return compassCategories.map(() => 0)
  }

  try {
    // Fetch all subcategories for these categories in one request
    const compassSubCategoriesResponse = await feathersClient.service('compass-sub-categories').find({
      query: {
        compassCategoryId: {
          $in: compassCategories.map(c => c.id),
        },
      },
    })
    // Normalize the response
    const compassSubCategoriesData = normalizeResponse(compassSubCategoriesResponse)

    if (compassSubCategoriesData.length === 0) {
      console.error('Failed to retrieve compass subcategories data or no compass subcategories found')
      return compassCategories.map(() => 0)
    }

    // Get all objective questions for this compass in one request
    const questionsResponse = await feathersClient.service('compass-questions').find({
      // query: { compassId, isObjective: true },
      query: { compassId, isObligatory: true },
    })

    // Normalize the response
    const questions = normalizeResponse(questionsResponse)

    if (questions.length === 0) {
      return compassCategories.map(() => 0)
    }

    // Get all answers for these questions in one request
    const answersResponse = await feathersClient.service('compass-question-answers').find({
      query: {
        compassQuestionId: {
          $in: questions.map(q => q.id),
        },
      },
    })

    // Normalize the response
    const answers = normalizeResponse(answersResponse)

    // Calculate ratings for each category
    return compassCategories.map(compassCategory => {
      // Get questions that belong to this category
      const categoryQuestions = getCompassCategoryQuestions(compassCategory.id, questions, compassSubCategoriesData)

      if (!categoryQuestions || categoryQuestions.length === 0) {
        return 0
      }

      // Filter answers for this category's questions
      const categoryAnswers = answers.filter(answer =>
        categoryQuestions.some(q => q.id === answer.compassQuestionId),
      )

      // Calculate rating using the existing function
      return calcCategoryQuestionRating(categoryQuestions, categoryAnswers, true)
    })
  } catch (error) {
    console.error('Error getting category objective question ratings:', error)
    return compassCategories.map(() => 0)
  }
}
